import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Save,
  UserCheck,
  UserPlus,
  UserX,
  Shield,
  Database,
  Brain,
  Server,
  Key,
  MessageSquare,
  Tag,
  Send,
  MapPin,
  Phone,
} from "lucide-react";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import { PrimaryButton, SecondaryButton } from "../common/ActionButtons";
import { useAuth } from "../../contexts/AuthContext";
import { facilitiesApi, facilityServicesApi } from "../../utils/api";
import {
  DEFAULT_INVITE_DESTINATION,
  INVITE_DESTINATIONS,
  SERVICE_TYPES,
} from "../../constants";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

// Product-grouped facility roles — add new products here as they come online.
// Each entry becomes a labeled section in the Manage Roles modal.
// Roles are stored as strings in user_service_access.facility_roles JSONB.
const FACILITY_ROLES_BY_PRODUCT = [
  {
    product: "rag",
    label: "Knowledge Base",
    Icon: Database,
    roles: [
      {
        value: "kb_steward",
        label: "KB Steward",
        description: "Can manage knowledge base documents and review queue",
        color: "bg-emerald-100 text-emerald-800",
      },
      {
        value: "broadcasts_composer",
        label: "Broadcasts Composer",
        description: "Can write and send broadcasts to facility members",
        color: "bg-orange-100 text-orange-800",
      },
    ],
  },
  {
    product: "fieldiq",
    label: "FieldIQ",
    Icon: MapPin,
    roles: [
      {
        value: "ld_leadership",
        label: "LD Leadership",
        description: "District leadership designation — elevated field access",
        color: "bg-purple-100 text-purple-800",
      },
      {
        value: "field_supervisor",
        label: "Field Supervisor",
        description: "Can manage field routes and canvasser activity",
        color: "bg-blue-100 text-blue-800",
      },
    ],
  },
  {
    product: "telegram_bot",
    label: "Telegram Bot",
    Icon: Send,
    roles: [
      {
        value: "telegram_moderator",
        label: "Telegram Moderator",
        description: "Can review and action Telegram Bot moderation violations",
        color: "bg-cyan-100 text-cyan-800",
      },
    ],
  },
  // Add new products here, e.g.:
  // { product: "voip", label: "VoiceIQ", Icon: Phone, roles: [...] }
];

// Maps facility_services.service_type → which FACILITY_ROLES_BY_PRODUCT groups are relevant.
// When the Manage Roles modal opens for a facility, only groups whose product key appears
// here (for that facility's active services) are shown. Add entries as new products launch.
const SERVICE_TO_ROLE_GROUPS = {
  rag: ["rag", "fieldiq"], // FieldIQ is built on the RAG service
  slm: [],
  private_llm: [],
  telegram_bot: ["telegram_bot"],
  voip: [], // placeholder — add VoiceIQ group and map here when roles are defined
};

// Flat lookup maps derived from the grouped structure above
const FACILITY_ROLES = FACILITY_ROLES_BY_PRODUCT.flatMap((p) => p.roles);
const ROLE_COLOR_MAP = Object.fromEntries(
  FACILITY_ROLES.map((r) => [r.value, r.color]),
);
const ROLE_LABEL_MAP = Object.fromEntries(
  FACILITY_ROLES.map((r) => [r.value, r.label]),
);

const RoleBadge = ({ role }) => {
  const colors = {
    admin: "bg-red-100 text-red-800",
    user: "bg-blue-100 text-blue-800",
    viewer: "bg-slate-100 text-slate-800",
    manager: "bg-purple-100 text-purple-800",
  };
  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[role] || colors.viewer}`}
    >
      {role}
    </span>
  );
};

const FacilityRolesBadges = ({ roles }) => {
  if (!roles || roles.length === 0)
    return <span className="text-xs text-slate-400">None</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span
          key={r}
          className={`px-2 py-0.5 text-xs font-medium rounded-full ${ROLE_COLOR_MAP[r] || "bg-slate-100 text-slate-700"}`}
          title={ROLE_LABEL_MAP[r] || r}
        >
          {ROLE_LABEL_MAP[r] || r}
        </span>
      ))}
    </div>
  );
};

const ServiceAccessBadges = ({ services }) => {
  if (!services || services.length === 0)
    return <span className="text-xs text-slate-400">No access</span>;
  const icons = {
    rag: Database,
    slm: Brain,
    private_llm: Server,
    telegram_bot: MessageSquare,
  };
  const colors = {
    rag: "text-blue-600",
    slm: "text-purple-600",
    private_llm: "text-amber-600",
    telegram_bot: "text-cyan-600",
  };

  return (
    <div className="flex space-x-1">
      {services.map((s, i) => {
        const Icon = icons[s.service_type] || Database;
        return (
          <div
            key={i}
            className="flex items-center"
            title={`${s.service_type}: ${s.access_role}`}
          >
            <Icon
              className={`w-3.5 h-3.5 ${colors[s.service_type] || "text-slate-500"}`}
            />
          </div>
        );
      })}
    </div>
  );
};

const FacilityUserManagement = () => {
  const { token, user, clients, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  // Where invited users land. Not window.location.origin: that sent an
  // invited user to whichever console the admin was in.
  const [inviteDestination, setInviteDestination] = useState(
    DEFAULT_INVITE_DESTINATION,
  );
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [showServiceAccess, setShowServiceAccess] = useState(null);
  const [showFacilityRoles, setShowFacilityRoles] = useState(null);
  const [facilitiesList, setFacilitiesList] = useState([]);
  // facility_id → string[] of active service_types for that facility
  const [facilityServicesMap, setFacilityServicesMap] = useState({});

  const [inviteForm, setInviteForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "user",
    password: "",
  });

  // "Add existing user" picker state — allUsers is admin-only (full system list)
  const [allUsers, setAllUsers] = useState([]);
  const [existingSearch, setExistingSearch] = useState("");
  const [addExistingForm, setAddExistingForm] = useState({
    email: "",
    role: "user",
    service_type: "rag",
  });

  const [accessForm, setAccessForm] = useState({
    facility_id: "",
    service_type: "rag",
    access_role: "user",
  });

  const [facilityRolesForm, setFacilityRolesForm] = useState({
    facility_id: "",
    roles: [],
  });

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    Promise.all([facilitiesApi.getAll(), facilityServicesApi.getAll()]).then(
      ([facilitiesData, servicesData]) => {
        setFacilitiesList(Array.isArray(facilitiesData) ? facilitiesData : []);
        // Build facility_id → active service_types[] lookup
        const svcMap = {};
        if (Array.isArray(servicesData)) {
          servicesData.forEach((svc) => {
            const fid = svc.facility_id || svc.facilityId;
            const stype = svc.service_type || svc.serviceType;
            if (fid && stype && svc.status === "active") {
              svcMap[fid] = [...(svcMap[fid] || []), stype];
            }
          });
        }
        setFacilityServicesMap(svcMap);
      },
    );
  }, []);

  useEffect(() => {
    if (!isAdmin && clients.length > 0 && !selectedClient) {
      setSelectedClient(clients[0].id);
    }
  }, [clients, isAdmin, selectedClient]);

  const fetchUsers = useCallback(async () => {
    if (!selectedClient && !isAdmin) return;
    setLoading(true);
    try {
      const isUuid =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          selectedClient,
        );
      const param = selectedClient
        ? isUuid
          ? `client_id=${selectedClient}`
          : `facility_id=${encodeURIComponent(selectedClient)}`
        : "";
      // include_disabled: a disabled user is exactly who you select in order
      // to re-enable them, so hiding them makes bulk enable impossible. Opt-in
      // on the endpoint because other callers — route assignee pickers — must
      // not see them.
      const url = param
        ? `${AUTH_URL}/facility-users?${param}&include_disabled=true`
        : `${AUTH_URL}/facility-users?include_disabled=true`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error("Error fetching facility users:", error);
    }
    setLoading(false);
  }, [selectedClient, isAdmin, token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/facility-users/invite`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...inviteForm,
          client_id: selectedClient,
          app_url: inviteDestination,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      fetchUsers();
      setShowInvite(false);
      setInviteForm({
        email: "",
        first_name: "",
        last_name: "",
        role: "user",
        password: "",
      });
      if (data.email_sent === false) {
        alert(
          "User added, but the invite email could not be sent. Check SMTP configuration.",
        );
      }
    } catch (error) {
      alert(error.message);
    }
    setLoading(false);
  };

  const openAddExisting = async () => {
    setAddExistingForm({ email: "", role: "user", service_type: "rag" });
    setExistingSearch("");
    setShowAddExisting(true);
    if (isAdmin) {
      try {
        // No facility param = full system user list (platform admin only)
        const res = await fetch(`${AUTH_URL}/facility-users`, { headers });
        const data = await res.json();
        setAllUsers(data.users || []);
      } catch (error) {
        console.error("Error fetching system users:", error);
      }
    }
  };

  const handleAddExisting = async (e) => {
    e.preventDefault();
    if (!addExistingForm.email) {
      alert("Select or enter a user email");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/facility-users/invite`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email: addExistingForm.email,
          role: addExistingForm.role,
          service_type: addExistingForm.service_type,
          existing_only: true,
          client_id: selectedClient,
          app_url: inviteDestination,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      fetchUsers();
      setShowAddExisting(false);
      if (data.email_sent === false) {
        alert(
          "Access granted, but the notification email could not be sent. Check SMTP configuration.",
        );
      }
    } catch (error) {
      alert(error.message);
    }
    setLoading(false);
  };

  const handleToggleActive = async (userId, currentActive) => {
    const action = currentActive ? "disable" : "enable";
    if (
      !window.confirm(
        `${action.charAt(0).toUpperCase() + action.slice(1)} this user?`,
      )
    )
      return;
    try {
      const res = await fetch(`${AUTH_URL}/facility-users/toggle`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: userId,
          is_active: !currentActive,
          client_id: selectedClient,
        }),
      });
      if (res.ok) fetchUsers();
    } catch (error) {
      console.error("Toggle failed:", error);
    }
  };

  const handleSetRole = async (userId, newRole) => {
    try {
      const res = await fetch(`${AUTH_URL}/facility-users/set-role`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: userId,
          client_id: selectedClient,
          role: newRole,
        }),
      });
      if (res.ok) fetchUsers();
    } catch (error) {
      console.error("Set role failed:", error);
    }
  };

  const handleGrantService = async (userId) => {
    if (!accessForm.facility_id) {
      alert("Please select a facility");
      return;
    }
    try {
      const res = await fetch(`${AUTH_URL}/facility-users/set-service-access`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: userId,
          facility_id: accessForm.facility_id,
          service_type: accessForm.service_type,
          access_role: accessForm.access_role,
        }),
      });
      if (res.ok) {
        fetchUsers();
        setShowServiceAccess(null);
      }
    } catch (error) {
      console.error("Grant service failed:", error);
    }
  };

  const handleSetFacilityRoles = async (userId) => {
    if (!facilityRolesForm.facility_id) {
      alert("Please select a facility");
      return;
    }
    try {
      const res = await fetch(`${AUTH_URL}/facility-users/set-facility-roles`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: userId,
          facility_id: facilityRolesForm.facility_id,
          facility_roles: facilityRolesForm.roles,
        }),
      });
      if (res.ok) {
        fetchUsers();
        setShowFacilityRoles(null);
        setFacilityRolesForm({ facility_id: "", roles: [] });
      }
    } catch (error) {
      console.error("Set facility roles failed:", error);
    }
  };

  // Returns the role groups relevant to a given facility's active products.
  // Falls back to all groups if the facility has no services loaded yet.
  const getFacilityRoleGroups = (facilityId) => {
    if (!facilityId) return FACILITY_ROLES_BY_PRODUCT;
    const serviceTypes = facilityServicesMap[facilityId] || [];
    if (serviceTypes.length === 0) return FACILITY_ROLES_BY_PRODUCT;
    const visibleProducts = new Set(
      serviceTypes.flatMap((st) => SERVICE_TO_ROLE_GROUPS[st] || []),
    );
    const groups = FACILITY_ROLES_BY_PRODUCT.filter((g) =>
      visibleProducts.has(g.product),
    );
    return groups.length > 0 ? groups : FACILITY_ROLES_BY_PRODUCT;
  };

  const openFacilityRolesModal = (u) => {
    // Pre-populate facility if one is currently selected and is a facility_id (not UUID)
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        selectedClient,
      );
    const preselectedFacility = selectedClient && !isUuid ? selectedClient : "";
    // Pre-populate existing roles for the preselected facility if available
    const existingRoles = u.facility_roles || [];
    setFacilityRolesForm({
      facility_id: preselectedFacility,
      roles: existingRoles,
    });
    setShowFacilityRoles(u.id);
  };

  const handleRevokeService = async (userId, facilityId, serviceType) => {
    if (!window.confirm(`Revoke ${serviceType} access for this user?`)) return;
    try {
      await fetch(`${AUTH_URL}/facility-users/set-service-access`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          user_id: userId,
          facility_id: facilityId,
          service_type: serviceType,
          revoke: true,
        }),
      });
      fetchUsers();
    } catch (error) {
      console.error("Revoke failed:", error);
    }
  };

  const handleResetPassword = async (userId) => {
    const newPw = window.prompt("Enter new password (min 8 characters):");
    if (!newPw || newPw.length < 8) {
      if (newPw) alert("Password must be at least 8 characters");
      return;
    }
    try {
      const res = await fetch(`${AUTH_URL}/set-password`, {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId, password: newPw }),
      });
      const data = await res.json();
      if (res.ok) alert("Password updated");
      else alert(data.error || "Failed");
    } catch (error) {
      console.error("Reset failed:", error);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.first_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.last_name || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ── Bulk enable / disable ──────────────────────────────────────────────
  //
  // One request, not one per user: a fifty-row disable should not be fifty
  // round trips that leave a half-applied result if the tab is closed midway.

  const isFacilityScope =
    selectedClient &&
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      selectedClient,
    );

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((u) => selectedIds.has(u.id));

  const toggleSelectAll = () =>
    setSelectedIds(
      allVisibleSelected ? new Set() : new Set(filteredUsers.map((u) => u.id)),
    );

  // Selection is by id, and the visible set changes with the search box — so
  // clear it whenever the facility changes rather than acting on rows the
  // admin can no longer see.
  useEffect(() => {
    setSelectedIds(new Set());
    setBulkResult(null);
  }, [selectedClient]);

  const runBulkToggle = async (isActive) => {
    const ids = filteredUsers
      .filter((u) => selectedIds.has(u.id))
      .filter((u) => (u.access_is_active !== false) !== isActive)
      .map((u) => u.id);
    if (!ids.length) {
      setBulkResult({
        text: `Nothing to ${isActive ? "enable" : "disable"} — the selected users are already ${isActive ? "enabled" : "disabled"}.`,
      });
      return;
    }
    if (
      !window.confirm(
        `${isActive ? "Enable" : "Disable"} ${ids.length} user${ids.length === 1 ? "" : "s"} at ${selectedClient}?\n\n` +
          (isActive
            ? "They regain access at their existing role."
            : "They are signed out immediately and lose access. Records are kept, so this can be undone."),
      )
    )
      return;
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${AUTH_URL}/facility-users/bulk-toggle`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          facility_id: selectedClient,
          user_ids: ids,
          is_active: isActive,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setBulkResult({
          text: data.error || `Failed (${res.status})`,
          err: true,
        });
      } else {
        setBulkResult({
          text:
            `${data.changed} user${data.changed === 1 ? "" : "s"} ${isActive ? "enabled" : "disabled"}.` +
            // Reported rather than folded into the count: an id with no
            // membership row at this facility is a real result, not a success.
            (data.missed?.length
              ? ` ${data.missed.length} had no access record here and were skipped.`
              : ""),
        });
        setSelectedIds(new Set());
        fetchUsers();
      }
    } catch (error) {
      setBulkResult({ text: "Network error — nothing changed.", err: true });
    }
    setBulkBusy(false);
  };

  // Existing-user picker candidates: exclude users already in the current
  // facility list, then filter by the modal's search term.
  const currentUserIds = new Set(users.map((u) => u.id));
  const candidateUsers = allUsers.filter((u) => {
    if (currentUserIds.has(u.id)) return false;
    const q = existingSearch.toLowerCase();
    return (
      u.email.toLowerCase().includes(q) ||
      `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase().includes(q)
    );
  });

  const getClientName = (id) => {
    const c = clients.find((cl) => cl.id === id);
    if (c) return c.name;
    const f = facilitiesList.find((fl) => fl.facility_id === id);
    if (f) return f.facility_name || f.facility_id;
    return id;
  };

  const dropdownOptions =
    isAdmin && facilitiesList.length > 0
      ? facilitiesList.map((f) => ({
          value: f.facility_id,
          label: f.facility_name || f.facility_id,
        }))
      : clients.map((c) => ({ value: c.id, label: c.name }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onRefresh={fetchUsers}
            placeholder="Search users..."
          />
          {(isAdmin || dropdownOptions.length > 1) && (
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {isAdmin && <option value="">All Facilities</option>}
              {dropdownOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
        {selectedClient && (
          <div className="flex items-center space-x-2">
            <SecondaryButton onClick={openAddExisting} icon={UserPlus}>
              Add Existing User
            </SecondaryButton>
            <PrimaryButton onClick={() => setShowInvite(true)} icon={Plus}>
              Invite User
            </PrimaryButton>
          </div>
        )}
      </div>

      {selectedClient && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          Managing users for: <strong>{getClientName(selectedClient)}</strong>
        </div>
      )}

      {/* Invite Modal */}
      <FormModal
        title="Invite User to Facility"
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
      >
        <form onSubmit={handleInvite} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                required
                value={inviteForm.email}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, email: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Temp Password *
              </label>
              <input
                type="text"
                required
                minLength={8}
                value={inviteForm.password}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, password: e.target.value })
                }
                placeholder="Min 8 characters"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                value={inviteForm.first_name}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, first_name: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={inviteForm.last_name}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, last_name: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Facility Role
              </label>
              <select
                value={inviteForm.role}
                onChange={(e) =>
                  setInviteForm({ ...inviteForm, role: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="viewer">Viewer</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Which product is this for?
              </label>
              <select
                value={inviteDestination}
                onChange={(e) => setInviteDestination(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {INVITE_DESTINATIONS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Their set-password email and sign-in link point here. Pick the
                app they will actually use — not this admin console.
              </p>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={() => setShowInvite(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Inviting..." : "Send Invite"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      {/* Add Existing User Modal */}
      <FormModal
        title="Add Existing User to Facility"
        isOpen={showAddExisting}
        onClose={() => setShowAddExisting(false)}
      >
        <form onSubmit={handleAddExisting} className="space-y-4">
          <p className="text-sm text-slate-500">
            Grant an existing system user access to{" "}
            <strong>{getClientName(selectedClient)}</strong>. No new account is
            created — the user is notified by email.
          </p>
          {isAdmin ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                User *
              </label>
              <input
                type="text"
                value={existingSearch}
                onChange={(e) => setExistingSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 mb-2"
              />
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                {candidateUsers.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-slate-400">
                    {allUsers.length === 0
                      ? "Loading users..."
                      : "No matching users (users already in this facility are hidden)."}
                  </div>
                ) : (
                  candidateUsers.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() =>
                        setAddExistingForm({
                          ...addExistingForm,
                          email: u.email,
                        })
                      }
                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${
                        addExistingForm.email === u.email ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="text-sm font-medium text-slate-900">
                        {u.first_name} {u.last_name}
                      </div>
                      <div className="text-xs text-slate-400">{u.email}</div>
                    </button>
                  ))
                )}
              </div>
              {addExistingForm.email && (
                <p className="text-xs text-slate-500 mt-1">
                  Selected: <strong>{addExistingForm.email}</strong>
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                User Email *
              </label>
              <input
                type="email"
                required
                value={addExistingForm.email}
                onChange={(e) =>
                  setAddExistingForm({
                    ...addExistingForm,
                    email: e.target.value,
                  })
                }
                placeholder="Existing user's email address"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Service
              </label>
              <select
                value={addExistingForm.service_type}
                onChange={(e) =>
                  setAddExistingForm({
                    ...addExistingForm,
                    service_type: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {SERVICE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Access Level
              </label>
              <select
                value={addExistingForm.role}
                onChange={(e) =>
                  setAddExistingForm({
                    ...addExistingForm,
                    role: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="viewer">Viewer</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={() => setShowAddExisting(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={loading || !addExistingForm.email}
              icon={UserPlus}
            >
              {loading ? "Adding..." : "Grant Access"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      {/* Service Access Modal */}
      <FormModal
        title={`Grant Service Access`}
        isOpen={!!showServiceAccess}
        onClose={() => setShowServiceAccess(null)}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Facility *
            </label>
            <select
              required
              value={accessForm.facility_id}
              onChange={(e) =>
                setAccessForm({ ...accessForm, facility_id: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select facility...</option>
              {facilitiesList
                .filter((f) => f.status === "active")
                .map((f) => (
                  <option
                    key={f.facility_id || f.id}
                    value={f.facility_id || f.facilityId}
                  >
                    {f.facility_name || f.facilityName} (
                    {f.facility_id || f.facilityId})
                  </option>
                ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Service
              </label>
              <select
                value={accessForm.service_type}
                onChange={(e) =>
                  setAccessForm({ ...accessForm, service_type: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {SERVICE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Access Level
              </label>
              <select
                value={accessForm.access_role}
                onChange={(e) =>
                  setAccessForm({ ...accessForm, access_role: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="viewer">Viewer (read-only)</option>
                <option value="user">User (can query)</option>
                <option value="admin">Admin (can configure)</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={() => setShowServiceAccess(null)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() => handleGrantService(showServiceAccess)}
              icon={Shield}
            >
              Grant Access
            </PrimaryButton>
          </div>
        </div>
      </FormModal>

      {/* Facility Roles Modal */}
      <FormModal
        title="Manage Facility Roles"
        isOpen={!!showFacilityRoles}
        onClose={() => setShowFacilityRoles(null)}
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Facility roles grant specific capabilities within the portal. Select
            the facility and check all roles that apply to this user.
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Facility *
            </label>
            <select
              required
              value={facilityRolesForm.facility_id}
              onChange={(e) =>
                setFacilityRolesForm({
                  ...facilityRolesForm,
                  facility_id: e.target.value,
                  roles: [],
                })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select facility...</option>
              {facilitiesList
                .filter((f) => f.status === "active")
                .map((f) => (
                  <option
                    key={f.facility_id || f.id}
                    value={f.facility_id || f.facilityId}
                  >
                    {f.facility_name || f.facilityName} (
                    {f.facility_id || f.facilityId})
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Roles (select all that apply)
            </label>
            <div className="space-y-4">
              {getFacilityRoleGroups(facilityRolesForm.facility_id).map(
                (group) => (
                  <div
                    key={group.product}
                    className="border border-slate-200 rounded-lg overflow-hidden"
                  >
                    <div className="flex items-center space-x-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
                      <group.Icon className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                        {group.label}
                      </span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {group.roles.map((r) => (
                        <label
                          key={r.value}
                          className="flex items-start space-x-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            checked={facilityRolesForm.roles.includes(r.value)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...facilityRolesForm.roles, r.value]
                                : facilityRolesForm.roles.filter(
                                    (x) => x !== r.value,
                                  );
                              setFacilityRolesForm({
                                ...facilityRolesForm,
                                roles: next,
                              });
                            }}
                            className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span
                              className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full mb-0.5 ${r.color}`}
                            >
                              {r.label}
                            </span>
                            <p className="text-xs text-slate-500">
                              {r.description}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={() => setShowFacilityRoles(null)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() => handleSetFacilityRoles(showFacilityRoles)}
              icon={Tag}
            >
              Save Roles
            </PrimaryButton>
          </div>
        </div>
      </FormModal>

      {/* Users Table */}
      {/* Bulk bar. Only with a facility selected: bulk-toggle scopes to one
          facility_id, and a client UUID would target nothing. */}
      {isFacilityScope && (selectedIds.size > 0 || bulkResult) && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg">
          {selectedIds.size > 0 && (
            <>
              <span className="text-sm text-slate-700">
                {selectedIds.size} selected
              </span>
              <button
                onClick={() => runBulkToggle(true)}
                disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-700 border border-green-300 rounded-lg hover:bg-green-50 disabled:opacity-50"
              >
                <UserCheck className="w-4 h-4" /> Enable
              </button>
              <button
                onClick={() => runBulkToggle(false)}
                disabled={bulkBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 disabled:opacity-50"
              >
                <UserX className="w-4 h-4" /> Disable
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Clear
              </button>
            </>
          )}
          {bulkResult && (
            <span
              className={`text-sm ${bulkResult.err ? "text-red-700" : "text-slate-600"}`}
            >
              {bulkResult.text}
            </span>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 w-10">
                {isFacilityScope && (
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-slate-300"
                    title="Select all users matching the current search"
                  />
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Access Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Facility Roles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Services
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  {selectedClient
                    ? "No users in this facility. Invite one to get started."
                    : "Select a facility to view users."}
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr
                  key={u.id}
                  className={`hover:bg-slate-50 ${u.access_is_active === false ? "bg-slate-50/70" : ""}`}
                >
                  <td className="px-4 py-4">
                    {isFacilityScope && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(u.id)}
                        onChange={() => toggleSelected(u.id)}
                        className="rounded border-slate-300"
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className={`text-sm font-medium ${u.access_is_active === false ? "text-slate-500" : "text-slate-900"}`}
                    >
                      {u.first_name} {u.last_name}
                    </div>
                    <div className="text-xs text-slate-400">{u.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={u.client_role || "user"}
                      onChange={(e) => handleSetRole(u.id, e.target.value)}
                      className="text-xs border border-slate-200 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <FacilityRolesBadges roles={u.facility_roles} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <ServiceAccessBadges services={u.services} />
                      {u.services &&
                        u.services.map((s, i) => (
                          <button
                            key={i}
                            onClick={() =>
                              handleRevokeService(
                                u.id,
                                s.facility_id,
                                s.service_type,
                              )
                            }
                            className="text-xs text-red-500 hover:text-red-700"
                            title={`Revoke ${s.service_type}`}
                          >
                            x
                          </button>
                        ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {/* Facility access first — that is what the toggle and
                        the bulk actions change. The account-wide flag is a
                        separate state and says so, rather than both sharing
                        the word "Disabled". */}
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        u.access_is_active === false
                          ? "bg-amber-100 text-amber-800"
                          : u.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                      }`}
                      title={
                        u.access_is_active === false
                          ? "No access to this facility. The record is kept — enable to restore it."
                          : u.is_active
                            ? ""
                            : "Account switched off platform-wide; facility access is still in place."
                      }
                    >
                      {u.access_is_active === false
                        ? "No access"
                        : u.is_active
                          ? "Active"
                          : "Account disabled"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleDateString()
                      : "Never"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-1">
                      <button
                        onClick={() => openFacilityRolesModal(u)}
                        className="p-1 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="Manage Facility Roles"
                      >
                        <Tag className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowServiceAccess(u.id)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Grant Service Access"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(u.id, u.is_active)}
                        className={`p-1 rounded transition-colors ${u.is_active ? "text-red-600 hover:bg-red-50" : "text-green-600 hover:bg-green-50"}`}
                        title={u.is_active ? "Disable User" : "Enable User"}
                      >
                        {u.is_active ? (
                          <UserX className="w-4 h-4" />
                        ) : (
                          <UserCheck className="w-4 h-4" />
                        )}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleResetPassword(u.id)}
                          className="p-1 text-slate-600 hover:bg-slate-50 rounded transition-colors"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FacilityUserManagement;
